import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { AlertTriangle, ArrowRight, RefreshCw, Loader2 } from 'lucide-react';

/**
 * Dashboard widget: payment-sync mismatches between an invoice's PAID status
 * and the customer's paid-date fields (which the customer portal reads).
 * Read-only view of the same check the nightly reconciliation runs.
 * Renders nothing when everything is in sync.
 */
export const PaymentSyncWidget = () => {
  const [data, setData] = useState({ count: 0, items: [] });
  const [reconciling, setReconciling] = useState(false);
  const navigate = useNavigate();

  const load = () => {
    api.get('/xero/payment-sync-status')
      .then((r) => setData(r.data || { count: 0, items: [] }))
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  const reconcileNow = async () => {
    setReconciling(true);
    try {
      const r = await api.post('/xero/reconcile-payments');
      const fixed = r.data?.auto_fixed || 0;
      toast.success(
        fixed > 0
          ? `Reconciled — ${fixed} invoice${fixed === 1 ? '' : 's'} pulled paid from Xero.`
          : 'Reconcile complete — no new payments found in Xero.'
      );
      load();
    } catch (e) {
      toast.error('Reconcile failed — please try again.');
    } finally {
      setReconciling(false);
    }
  };

  if (!data.items?.length) return null;

  return (
    <Card className="border-amber-300 bg-amber-50/60" data-testid="payment-sync-widget">
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-slate-900" data-testid="payment-sync-count">
              {data.count} payment sync {data.count === 1 ? 'gap' : 'gaps'}
            </p>
            <p className="text-sm text-slate-600 mt-0.5">
              Invoice paid-status and the customer's payment record are out of sync — these
              won't show correctly on the customer portal until reconciled.
            </p>
          </div>
          <Button
            size="sm"
            className="bg-amber-600 hover:bg-amber-700 text-white flex-shrink-0"
            onClick={reconcileNow}
            disabled={reconciling}
            data-testid="payment-sync-reconcile-btn"
          >
            {reconciling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
            {reconciling ? 'Reconciling…' : 'Reconcile now'}
          </Button>
        </div>

        <ul className="space-y-2">
          {data.items.slice(0, 6).map((it) => (
            <li
              key={it.key}
              className="flex items-center justify-between gap-3 bg-white border border-amber-100 rounded-lg px-3 py-2"
              data-testid={`payment-sync-item-${it.customer_id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{it.customer_name || 'Customer'}</p>
                <p className="text-xs text-slate-500 truncate">{it.message}</p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-amber-300 text-amber-700 hover:bg-amber-50 flex-shrink-0"
                onClick={() => navigate(`/customers/${it.customer_id}`)}
                data-testid={`payment-sync-open-btn-${it.customer_id}`}
              >
                Open <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};

export default PaymentSyncWidget;
